import { useCallback, useEffect, useRef, useState } from "react";

const ICE_SERVERS = [
    {
        urls: ["stun:stun.l.google.com:19302", "stun:global.stun.twilio.com:3478"],
    },
];

const DISCONNECTED_GRACE_MS = 8000;

export default function useVideoCall({ wsRef, currentUserId }) {
    const [callState, setCallState] = useState("idle");
    const [incomingCall, setIncomingCall] = useState(null);
    const [activeCall, setActiveCall] = useState(null);
    const [error, setError] = useState("");

    const [isMicEnabled, setIsMicEnabled] = useState(true);
    const [isCameraEnabled, setIsCameraEnabled] = useState(true);
    const [isScreenSharing, setIsScreenSharing] = useState(false);

    const pcRef = useRef(null);
    const localStreamRef = useRef(null);
    const remoteStreamRef = useRef(null);
    const cameraStreamRef = useRef(null);
    const screenStreamRef = useRef(null);

    const localVideoRef = useRef(null);
    const remoteVideoRef = useRef(null);

    const pendingCandidatesRef = useRef([]);
    const activeCallRef = useRef(null);
    const incomingCallRef = useRef(null);
    const disconnectTimerRef = useRef(null);

    const lastRemoteAnswerSdpRef = useRef("");
    const lastRemoteOfferSdpRef = useRef("");

    const syncActiveCall = useCallback((valueOrUpdater) => {
        setActiveCall((prev) => {
            const next =
                typeof valueOrUpdater === "function" ? valueOrUpdater(prev) : valueOrUpdater;
            activeCallRef.current = next;
            return next;
        });
    }, []);

    const syncIncomingCall = useCallback((valueOrUpdater) => {
        setIncomingCall((prev) => {
            const next =
                typeof valueOrUpdater === "function" ? valueOrUpdater(prev) : valueOrUpdater;
            incomingCallRef.current = next;
            return next;
        });
    }, []);

    const clearDisconnectTimer = useCallback(() => {
        if (disconnectTimerRef.current) {
            clearTimeout(disconnectTimerRef.current);
            disconnectTimerRef.current = null;
        }
    }, []);

    const stopTracks = useCallback((stream) => {
        if (!stream) return;
        stream.getTracks().forEach((track) => {
            try {
                track.stop();
            } catch { }
        });
    }, []);

    const resetSignalCaches = useCallback(() => {
        lastRemoteAnswerSdpRef.current = "";
        lastRemoteOfferSdpRef.current = "";
        pendingCandidatesRef.current = [];
    }, []);

    const attachLocalStream = useCallback((stream) => {
        if (!localVideoRef.current || !stream) return;
        if (localVideoRef.current.srcObject !== stream) {
            localVideoRef.current.srcObject = stream;
        }
        localVideoRef.current.play().catch(() => { });
    }, []);

    const attachRemoteStream = useCallback((stream) => {
        if (!remoteVideoRef.current || !stream) return;
        if (remoteVideoRef.current.srcObject !== stream) {
            remoteVideoRef.current.srcObject = stream;
        }
        remoteVideoRef.current.play().catch(() => { });
    }, []);

    const getAudioSender = useCallback(() => {
        const pc = pcRef.current;
        if (!pc) return null;
        return pc.getSenders().find((sender) => sender.track?.kind === "audio") || null;
    }, []);

    const getVideoSender = useCallback(() => {
        const pc = pcRef.current;
        if (!pc) return null;
        return pc.getSenders().find((sender) => sender.track?.kind === "video") || null;
    }, []);

    const applyTrackStateToLocalStream = useCallback(() => {
        const localStream = localStreamRef.current;
        if (!localStream) return;

        localStream.getAudioTracks().forEach((track) => {
            track.enabled = isMicEnabled;
        });

        localStream.getVideoTracks().forEach((track) => {
            track.enabled = isCameraEnabled;
        });
    }, [isMicEnabled, isCameraEnabled]);

    const cleanupPeer = useCallback(() => {
        clearDisconnectTimer();

        try {
            if (pcRef.current) {
                pcRef.current.ontrack = null;
                pcRef.current.onicecandidate = null;
                pcRef.current.onconnectionstatechange = null;
                pcRef.current.close();
            }
        } catch { }

        pcRef.current = null;

        stopTracks(localStreamRef.current);
        stopTracks(cameraStreamRef.current);
        stopTracks(screenStreamRef.current);
        stopTracks(remoteStreamRef.current);

        localStreamRef.current = null;
        remoteStreamRef.current = null;
        cameraStreamRef.current = null;
        screenStreamRef.current = null;

        if (localVideoRef.current) {
            localVideoRef.current.srcObject = null;
        }

        if (remoteVideoRef.current) {
            remoteVideoRef.current.srcObject = null;
        }

        resetSignalCaches();
        setIsMicEnabled(true);
        setIsCameraEnabled(true);
        setIsScreenSharing(false);
    }, [clearDisconnectTimer, stopTracks, resetSignalCaches]);

    const endWithCleanup = useCallback(
        (nextState = "ended", nextError = "") => {
            if (nextError) {
                setError(nextError);
            } else {
                setError("");
            }

            cleanupPeer();
            syncIncomingCall(null);
            syncActiveCall(null);
            setCallState(nextState);

            if (nextState === "ended") {
                window.setTimeout(() => {
                    setCallState((prev) => (prev === "ended" ? "idle" : prev));
                    setError("");
                }, 800);
            }
        },
        [cleanupPeer, syncIncomingCall, syncActiveCall]
    );

    const ensureLocalMedia = useCallback(async () => {
        if (localStreamRef.current) {
            applyTrackStateToLocalStream();
            attachLocalStream(localStreamRef.current);
            return localStreamRef.current;
        }

        const stream = await navigator.mediaDevices.getUserMedia({
            audio: true,
            video: {
                facingMode: "user",
                width: { ideal: 1280 },
                height: { ideal: 720 },
            },
        });

        cameraStreamRef.current = stream;
        localStreamRef.current = stream;

        stream.getAudioTracks().forEach((track) => {
            track.enabled = isMicEnabled;
        });

        stream.getVideoTracks().forEach((track) => {
            track.enabled = isCameraEnabled;
        });

        attachLocalStream(stream);
        return stream;
    }, [attachLocalStream, applyTrackStateToLocalStream, isMicEnabled, isCameraEnabled]);

    const flushPendingCandidates = useCallback(async () => {
        const pc = pcRef.current;
        if (!pc || !pc.remoteDescription) return;

        const candidates = [...pendingCandidatesRef.current];
        pendingCandidatesRef.current = [];

        for (const candidate of candidates) {
            try {
                await pc.addIceCandidate(new RTCIceCandidate(candidate));
            } catch (err) {
                console.error("Failed to add pending ICE candidate:", err);
            }
        }
    }, []);

    const createPeerConnection = useCallback(
        ({ convId, otherUserId }) => {
            if (pcRef.current) return pcRef.current;

            const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
            pcRef.current = pc;

            remoteStreamRef.current = new MediaStream();
            attachRemoteStream(remoteStreamRef.current);

            pc.ontrack = (event) => {
                const incomingStream = event.streams?.[0];

                if (incomingStream) {
                    remoteStreamRef.current = incomingStream;
                    attachRemoteStream(incomingStream);
                    return;
                }

                if (!remoteStreamRef.current) {
                    remoteStreamRef.current = new MediaStream();
                }

                if (event.track) {
                    const exists = remoteStreamRef.current
                        .getTracks()
                        .some((track) => track.id === event.track.id);

                    if (!exists) {
                        remoteStreamRef.current.addTrack(event.track);
                    }
                }

                attachRemoteStream(remoteStreamRef.current);
            };

            pc.onicecandidate = (event) => {
                if (!event.candidate || !wsRef?.current) return;

                wsRef.current.webrtcSignal({
                    convId,
                    to: otherUserId,
                    kind: "ice-candidate",
                    candidate: event.candidate,
                    callType: "video",
                });
            };

            pc.onconnectionstatechange = () => {
                const state = pc.connectionState;

                if (state === "connected") {
                    clearDisconnectTimer();
                    setError("");
                    setCallState("connected");
                    return;
                }

                if (state === "disconnected") {
                    clearDisconnectTimer();
                    disconnectTimerRef.current = window.setTimeout(() => {
                        if (pcRef.current?.connectionState === "disconnected") {
                            endWithCleanup("ended", "Video call disconnected");
                        }
                    }, DISCONNECTED_GRACE_MS);
                    return;
                }

                if (state === "failed") {
                    endWithCleanup("ended", "Video call failed");
                    return;
                }

                if (state === "closed") {
                    endWithCleanup("ended", "");
                }
            };

            return pc;
        },
        [wsRef, clearDisconnectTimer, endWithCleanup, attachRemoteStream]
    );

    const addLocalTracksToPeer = useCallback((pc, stream) => {
        const existingSenders = pc.getSenders();

        stream.getAudioTracks().forEach((track) => {
            const audioSender = existingSenders.find((sender) => sender.track?.kind === "audio");
            if (!audioSender) {
                pc.addTrack(track, stream);
            }
        });

        stream.getVideoTracks().forEach((track) => {
            const videoSender = existingSenders.find((sender) => sender.track?.kind === "video");
            if (!videoSender) {
                pc.addTrack(track, stream);
            }
        });
    }, []);

    const startVideoCall = useCallback(
        async ({
            convId,
            to,
            propertyId,
            visitorId,
            sessionId,
            userType,
            userId,
            userIcon,
            userName,
            pageName,
            pageURL,
        }) => {
            setError("");
            resetSignalCaches();

            if (!wsRef?.current) {
                const msg = "Socket instance missing";
                setError(msg);
                return { ok: false, reason: msg };
            }

            if (!wsRef.current.isReady?.()) {
                const msg = "Socket not connected";
                setError(msg);
                return { ok: false, reason: msg };
            }

            const ok = wsRef.current.inviteVideoCall({
                convId,
                to,
                propertyId,
                visitorId,
                sessionId,
                userType,
                userId,
                userIcon,
                userName,
                pageName,
                pageURL,
            });

            if (!ok) {
                const msg = "Unable to send video call invite";
                setError(msg);
                return { ok: false, reason: msg };
            }

            syncActiveCall({
                callId: "",
                callType: "video",
                convId,
                from: currentUserId,
                to,
                propertyId,
                visitorId,
                sessionId,
                userType,
                userId,
                userIcon,
                userName,
                pageName,
                pageURL,
            });

            setCallState("outgoing");
            return { ok: true };
        },
        [wsRef, currentUserId, resetSignalCaches, syncActiveCall]
    );

    const acceptIncomingCall = useCallback(async () => {
        const call = incomingCallRef.current;
        if (!call) return;

        try {
            setError("");
            setCallState("connecting");
            syncActiveCall(call);
            syncIncomingCall(null);

            const stream = await ensureLocalMedia();
            const pc = createPeerConnection({
                convId: call.convId,
                otherUserId: call.from,
            });

            addLocalTracksToPeer(pc, stream);

            wsRef?.current?.acceptVideoCall({
                callId: call.callId,
                convId: call.convId,
                to: call.from,
            });
        } catch (err) {
            console.error("Failed to accept video call:", err);
            setError(err?.message || "Failed to accept video call");
            cleanupPeer();
            syncIncomingCall(null);
            syncActiveCall(null);
            setCallState("idle");
        }
    }, [
        ensureLocalMedia,
        createPeerConnection,
        addLocalTracksToPeer,
        wsRef,
        cleanupPeer,
        syncIncomingCall,
        syncActiveCall,
    ]);

    const rejectIncomingCall = useCallback(() => {
        const call = incomingCallRef.current;
        if (!call) return;

        try {
            wsRef?.current?.rejectVideoCall({
                callId: call.callId,
                convId: call.convId,
                to: call.from,
                reason: "rejected",
            });
        } catch (err) {
            console.error("Failed to reject video call:", err);
        }

        syncIncomingCall(null);
        setError("");
        setCallState("idle");
    }, [wsRef, syncIncomingCall]);

    const endCall = useCallback(() => {
        const call = activeCallRef.current || incomingCallRef.current;
        const otherUserId = call?.from === currentUserId ? call?.to : call?.from;

        try {
            if (call?.convId && otherUserId) {
                wsRef?.current?.endVideoCall({
                    callId: call?.callId || "",
                    convId: call.convId,
                    to: otherUserId,
                });
            }
        } catch (err) {
            console.error("Failed to signal endVideoCall:", err);
        }

        endWithCleanup("ended", "");
    }, [currentUserId, wsRef, endWithCleanup]);

    const toggleMic = useCallback(() => {
        const localStream = localStreamRef.current;
        if (!localStream) return;

        const nextEnabled = !isMicEnabled;
        localStream.getAudioTracks().forEach((track) => {
            track.enabled = nextEnabled;
        });
        setIsMicEnabled(nextEnabled);
    }, [isMicEnabled]);

    const toggleCamera = useCallback(() => {
        const localStream = localStreamRef.current;
        if (!localStream) return;

        const nextEnabled = !isCameraEnabled;
        localStream.getVideoTracks().forEach((track) => {
            track.enabled = nextEnabled;
        });
        setIsCameraEnabled(nextEnabled);
    }, [isCameraEnabled]);

    const stopScreenShare = useCallback(async () => {
        const screenStream = screenStreamRef.current;
        const cameraStream = cameraStreamRef.current;
        const videoSender = getVideoSender();

        if (!screenStream || !cameraStream || !videoSender) {
            setIsScreenSharing(false);
            return;
        }

        const cameraTrack = cameraStream.getVideoTracks()[0];
        if (!cameraTrack) {
            setIsScreenSharing(false);
            return;
        }

        await videoSender.replaceTrack(cameraTrack);

        const audioTrack = localStreamRef.current?.getAudioTracks?.()[0] || null;
        const rebuiltLocalStream = new MediaStream([
            ...(audioTrack ? [audioTrack] : []),
            cameraTrack,
        ]);

        localStreamRef.current = rebuiltLocalStream;
        attachLocalStream(rebuiltLocalStream);

        stopTracks(screenStream);
        screenStreamRef.current = null;
        setIsScreenSharing(false);

        cameraTrack.enabled = isCameraEnabled;
    }, [getVideoSender, attachLocalStream, stopTracks, isCameraEnabled]);

    const toggleScreenShare = useCallback(async () => {
        try {
            if (isScreenSharing) {
                await stopScreenShare();
                return;
            }

            const pc = pcRef.current;
            const currentLocalStream = localStreamRef.current;

            if (!pc || !currentLocalStream) return;

            const displayStream = await navigator.mediaDevices.getDisplayMedia({
                video: true,
                audio: false,
            });

            const screenTrack = displayStream.getVideoTracks()[0];
            const videoSender = getVideoSender();
            const currentAudioTrack = currentLocalStream.getAudioTracks()[0] || null;

            if (!screenTrack || !videoSender) {
                stopTracks(displayStream);
                return;
            }

            screenTrack.onended = () => {
                stopScreenShare().catch(() => { });
            };

            await videoSender.replaceTrack(screenTrack);

            const rebuiltLocalStream = new MediaStream([
                ...(currentAudioTrack ? [currentAudioTrack] : []),
                screenTrack,
            ]);

            screenStreamRef.current = displayStream;
            localStreamRef.current = rebuiltLocalStream;

            attachLocalStream(rebuiltLocalStream);
            setIsScreenSharing(true);
        } catch (err) {
            console.error("Screen sharing failed:", err);
            setError(err?.message || "Screen sharing failed");
        }
    }, [
        isScreenSharing,
        getVideoSender,
        attachLocalStream,
        stopTracks,
        stopScreenShare,
    ]);

    const handleSocketEvent = useCallback(
        async (type, data) => {
            if (!data) return;

            try {
                if (type === "call:incoming") {
                    if (data?.callType !== "video") return;
                    syncIncomingCall(data);
                    setCallState("incoming");
                    return;
                }

                if (type === "call:outgoing") {
                    if (data?.callType !== "video") return;
                    syncActiveCall(data);
                    setCallState("outgoing");
                    return;
                }

                if (type === "call:accepted") {
                    const currentCall = activeCallRef.current || data;
                    if ((currentCall?.callType || data?.callType) !== "video") return;

                    syncActiveCall((prev) => ({
                        ...(prev || {}),
                        ...(currentCall || {}),
                        ...data,
                        callId: data.callId || prev?.callId || "",
                        callType: "video",
                    }));

                    setCallState("connecting");

                    const stream = await ensureLocalMedia();
                    const pc = createPeerConnection({
                        convId: currentCall.convId || data.convId,
                        otherUserId: data.from,
                    });

                    addLocalTracksToPeer(pc, stream);

                    if (pc.signalingState !== "stable") {
                        return;
                    }

                    const offer = await pc.createOffer({
                        offerToReceiveAudio: true,
                        offerToReceiveVideo: true,
                    });

                    await pc.setLocalDescription(offer);

                    wsRef?.current?.webrtcSignal({
                        convId: currentCall.convId || data.convId,
                        to: data.from,
                        kind: "offer",
                        sdp: offer,
                        callType: "video",
                    });
                    return;
                }

                if (type === "call:rejected") {
                    const currentCall = activeCallRef.current || incomingCallRef.current || data;
                    if ((currentCall?.callType || data?.callType) !== "video") return;
                    endWithCleanup("ended", "Video call rejected");
                    return;
                }

                if (type === "call:ended") {
                    const currentCall = activeCallRef.current || incomingCallRef.current || data;
                    if ((currentCall?.callType || data?.callType) !== "video") return;
                    endWithCleanup("ended", "");
                    return;
                }

                if (type !== "webrtc:signal") return;
                if (data?.callType && data.callType !== "video") return;

                const kind = data?.kind;

                if (kind === "offer") {
                    const incomingOfferSdp = data?.sdp?.sdp || "";
                    if (
                        incomingOfferSdp &&
                        incomingOfferSdp === lastRemoteOfferSdpRef.current
                    ) {
                        return;
                    }

                    lastRemoteOfferSdpRef.current = incomingOfferSdp;

                    const stream = await ensureLocalMedia();
                    const pc = createPeerConnection({
                        convId: data.convId,
                        otherUserId: data.from,
                    });

                    if (pc.signalingState !== "stable") {
                        return;
                    }

                    addLocalTracksToPeer(pc, stream);

                    await pc.setRemoteDescription(new RTCSessionDescription(data.sdp));
                    await flushPendingCandidates();

                    const answer = await pc.createAnswer();
                    await pc.setLocalDescription(answer);

                    wsRef?.current?.webrtcSignal({
                        convId: data.convId,
                        to: data.from,
                        kind: "answer",
                        sdp: answer,
                        callType: "video",
                    });

                    setCallState("connecting");
                    return;
                }

                if (kind === "answer") {
                    const pc = pcRef.current;
                    if (!pc) return;

                    const incomingAnswerSdp = data?.sdp?.sdp || "";

                    if (
                        incomingAnswerSdp &&
                        incomingAnswerSdp === lastRemoteAnswerSdpRef.current
                    ) {
                        return;
                    }

                    lastRemoteAnswerSdpRef.current = incomingAnswerSdp;

                    if (pc.signalingState !== "have-local-offer") {
                        return;
                    }

                    await pc.setRemoteDescription(new RTCSessionDescription(data.sdp));
                    await flushPendingCandidates();
                    return;
                }

                if (kind === "ice-candidate") {
                    if (!data.candidate) return;

                    if (!pcRef.current || !pcRef.current.remoteDescription) {
                        pendingCandidatesRef.current.push(data.candidate);
                        return;
                    }

                    try {
                        await pcRef.current.addIceCandidate(
                            new RTCIceCandidate(data.candidate)
                        );
                    } catch (err) {
                        console.error("Failed to add live ICE candidate:", err);
                    }
                }
            } catch (err) {
                console.error("Video call socket handler failed:", err, { type, data });
                setError(err?.message || "Video call failed");
            }
        },
        [
            ensureLocalMedia,
            createPeerConnection,
            addLocalTracksToPeer,
            flushPendingCandidates,
            wsRef,
            endWithCleanup,
            syncIncomingCall,
            syncActiveCall,
        ]
    );

    useEffect(() => {
        if (localStreamRef.current) {
            attachLocalStream(localStreamRef.current);
        }
    }, [callState, attachLocalStream]);

    useEffect(() => {
        if (remoteStreamRef.current) {
            attachRemoteStream(remoteStreamRef.current);
        }
    }, [callState, attachRemoteStream]);

    useEffect(() => {
        return () => {
            cleanupPeer();
        };
    }, [cleanupPeer]);

    return {
        callState,
        incomingCall,
        activeCall,
        error,
        localVideoRef,
        remoteVideoRef,
        isMicEnabled,
        isCameraEnabled,
        isScreenSharing,
        startVideoCall,
        acceptIncomingCall,
        rejectIncomingCall,
        endCall,
        toggleMic,
        toggleCamera,
        toggleScreenShare,
        handleSocketEvent,
        clearError: () => setError(""),
    };
}